import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  adminCarService,
  adminCarFeaturesService,
  adminCarBenefitsService,
  adminCarSpecificationsService,
  adminCarContentService,
  adminCarPublishService,
} from '../../services/adminService';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const toAbs = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_BASE}${path}`;
};

// ── Shared inline styles for form fields (immune to CSS overrides) ─────────────
const FIELD_STYLE = {
  display: 'block',
  width: '100%',
  padding: '10px 12px',
  border: '2px solid #d0d0d0',
  borderRadius: '7px',
  fontSize: '14px',
  fontFamily: 'inherit',
  background: '#ffffff',
  color: '#0A0A0A',
  outline: 'none',
  boxSizing: 'border-box',
  marginTop: '4px',
  minHeight: '42px',
  lineHeight: '1.4',
  appearance: 'auto',
  WebkitAppearance: 'auto',
};

const LABEL_STYLE = {
  display: 'block',
  fontSize: '11px',
  fontWeight: '700',
  color: '#003566',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '4px',
};

// ── FormGroup helper — fully inline, zero CSS dependency ──────────────────────
const FG = ({ label, children, full }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    gridColumn: full ? '1 / -1' : undefined,
  }}>
    <label style={LABEL_STYLE}>{label}</label>
    {React.Children.map(children, child => {
      if (!child) return null;
      const tag = child.type;
      // Only clone native input/select/textarea to inject inline style
      if (tag === 'input' || tag === 'select' || tag === 'textarea') {
        const extra = tag === 'textarea' ? { minHeight: '80px', resize: 'vertical' } : {};
        const isCheckbox = child.props.type === 'checkbox';
        const isFile     = child.props.type === 'file';
        if (isCheckbox) return child; // checkboxes keep their natural style
        const fieldStyle = isFile
          ? { ...FIELD_STYLE, ...extra, borderStyle: 'dashed', background: '#fafafa', cursor: 'pointer' }
          : { ...FIELD_STYLE, ...extra };
        return React.cloneElement(child, { style: { ...fieldStyle, ...(child.props.style || {}) } });
      }
      return child;
    })}
  </div>
);

// ── Lightbox ──────────────────────────────────────────────────────────────────
const Lightbox = ({ src, type = 'image', onClose }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', handler); };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <button onClick={onClose} style={{ position: 'fixed', top: 20, right: 24, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', fontSize: 26, cursor: 'pointer', borderRadius: '50%', width: 46, height: 46, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000 }}>✕</button>
      <div onClick={e => e.stopPropagation()} style={{ maxWidth: '95vw', maxHeight: '95vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {type === 'video'
          ? <video controls autoPlay style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 10 }} src={toAbs(src)} />
          : <img src={toAbs(src)} alt="fullscreen" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 10, objectFit: 'contain' }} />}
      </div>
    </div>,
    document.body
  );
};

// ── Pagination ────────────────────────────────────────────────────────────────
const Pagination = ({ page, totalPages, total, perPage, onPageChange }) => {
  if (!totalPages || totalPages <= 1) return null;
  const from = (page - 1) * perPage + 1;
  const to   = Math.min(page * perPage, total);
  const delta = 2;
  const pages = [];
  for (let i = Math.max(1, page - delta); i <= Math.min(totalPages, page + delta); i++) pages.push(i);
  const bs = (active, disabled) => ({
    minWidth: 34, height: 34, padding: '0 8px',
    border: `1px solid ${active ? '#003566' : '#d9d9d9'}`,
    background: active ? '#003566' : '#fff', borderRadius: 6,
    cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13,
    color: active ? '#FFD60A' : '#333', fontWeight: active ? 700 : 400,
    opacity: disabled ? 0.4 : 1,
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: '#fff', borderRadius: 10, marginBottom: 10, border: '1px solid #e8e8e8', flexWrap: 'wrap', gap: 8 }}>
      <span style={{ fontSize: 13, color: '#666' }}>Showing <strong>{from}</strong>–<strong>{to}</strong> of <strong>{total}</strong> cars</span>
      <div style={{ display: 'flex', gap: 3 }}>
        <button onClick={() => onPageChange(1)} disabled={page === 1} style={bs(false, page === 1)}>«</button>
        <button onClick={() => onPageChange(page - 1)} disabled={page === 1} style={bs(false, page === 1)}>‹</button>
        {pages[0] > 1 && <><button onClick={() => onPageChange(1)} style={bs(false, false)}>1</button>{pages[0] > 2 && <span style={{ padding: '0 3px', color: '#999' }}>…</span>}</>}
        {pages.map(p => <button key={p} onClick={() => onPageChange(p)} style={bs(p === page, false)}>{p}</button>)}
        {pages[pages.length - 1] < totalPages && <>{pages[pages.length - 1] < totalPages - 1 && <span style={{ padding: '0 3px', color: '#999' }}>…</span>}<button onClick={() => onPageChange(totalPages)} style={bs(false, false)}>{totalPages}</button></>}
        <button onClick={() => onPageChange(page + 1)} disabled={page === totalPages} style={bs(false, page === totalPages)}>›</button>
        <button onClick={() => onPageChange(totalPages)} disabled={page === totalPages} style={bs(false, page === totalPages)}>»</button>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const CarsManagement = () => {
  const [cars, setCars]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);
  const perPage = 12;

  const [showModal, setShowModal]     = useState(false);
  const [modalMode, setModalMode]     = useState('create');
  const [selectedCar, setSelectedCar] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
const [carToDelete,       setCarToDelete]       = useState(null);
const [deleteLoading,     setDeleteLoading]     = useState(false);

  const loadCars = useCallback(async (currentPage = 1, currentFilter = 'all') => {
    try {
      setLoading(true);
      const params = { page: currentPage, per_page: perPage };
      if (currentFilter !== 'all') params.status = currentFilter;
      const response = await adminCarService.getAllCars(params);
      setCars(response.data || []);
      setTotal(response.total || 0);
      setTotalPages(response.total_pages || 1);
      setPage(response.page || currentPage);
    } catch (error) {
      console.error('Error loading cars:', error);
      alert('Failed to load cars');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCars(1, 'all'); }, [loadCars]);

  const handleFilterChange = (f) => { setFilter(f); setSearchTerm(''); loadCars(1, f); };
  const handlePageChange   = (p) => loadCars(p, filter);
  const handleCreate  = () => { setSelectedCar(null); setModalMode('create');  setShowModal(true); };
  const handleEdit    = (c) => { setSelectedCar(c);   setModalMode('edit');    setShowModal(true); };
  const handleView    = (c) => { setSelectedCar(c);   setModalMode('view');    setShowModal(true); };
  const handleContent = (c) => { setSelectedCar(c);   setModalMode('content'); setShowModal(true); };


  const handleDelete = async (id) => {
    // Use a custom confirm — but since we're keeping it simple, 
    // the window.confirm is replaced with a two-step inline state
    setCarToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!carToDelete) return;
    setDeleteLoading(true);
    try {
      await adminCarService.deleteCar(carToDelete);
      setShowDeleteConfirm(false);
      setCarToDelete(null);
      setDeleteLoading(false);
      loadCars(page, filter);
    } catch (err) {
      setDeleteLoading(false);
      alert('Failed to delete car: ' + (err.response?.data?.message || err.message));
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setCarToDelete(null);
  };
  const handleApprove  = async (id) => { try { await adminCarService.approveCar(id);  alert('Approved');       loadCars(page, filter); } catch { alert('Failed'); } };
  const handleReject   = async (id) => { try { await adminCarService.rejectCar(id);   alert('Rejected');       loadCars(page, filter); } catch { alert('Failed'); } };
  const handleMarkSold = async (id) => { try { await adminCarService.markSold(id);    alert('Marked as sold'); loadCars(page, filter); } catch { alert('Failed'); } };
  const handleReserve  = async (id) => { try { await adminCarService.reserveCar(id);  alert('Reserved');       loadCars(page, filter); } catch { alert('Failed'); } };

  const displayedCars = cars.filter(car => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return car.title?.toLowerCase().includes(q) || car.brand?.toLowerCase().includes(q) || car.model?.toLowerCase().includes(q);
  });

  const statusColors = { available: '#28a745', sold: '#6c757d', reserved: '#ffc107', under_inspection: '#17a2b8' };
  const getStatusBadge = (status) => {
    const c = statusColors[status] || '#999';
    return <span className="status-badge" style={{ background: `${c}20`, color: c }}>{status?.replace(/_/g, ' ')}</span>;
  };

  if (loading) return <div className="management-loading"><div className="spinner-large"></div><p>Loading cars...</p></div>;

  return (
    <div className="cars-management" style={{ width: '100%', minWidth: 0 }}>
      <div className="management-header" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div><h1>Cars Management</h1><p>Manage all car listings and inventory</p></div>
        <button className="add-btn" onClick={handleCreate}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          Add New Car
        </button>
      </div>

      <div className="management-filters">
        <div className="search-box" style={{ flex: 1, maxWidth: 400 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" /><path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          <input type="text" placeholder="Search by title, brand, or model..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <div className="filter-tabs" style={{ flexWrap: 'wrap' }}>
          {['all', 'available', 'sold', 'reserved', 'under_inspection'].map(s => (
            <button key={s} className={`filter-tab ${filter === s ? 'active' : ''}`} onClick={() => handleFilterChange(s)}>
              {s === 'all' ? 'All' : s.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>

      <Pagination page={page} totalPages={totalPages} total={total} perPage={perPage} onPageChange={handlePageChange} />

      <div style={{ background: '#fff', borderRadius: 12, overflowX: 'auto', width: '100%' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
          <thead style={{ background: '#f5f5f5' }}>
            <tr>
              {['Image', 'Car Details', 'Price', 'Year', 'KM Driven', 'Location', 'Status', 'Actions'].map(h => (
                <th key={h} style={{ padding: '12px 10px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayedCars.map(car => (
              <tr key={car.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                <td style={{ padding: '10px' }}>
                  {car.images_urls?.length > 0
                    ? <img src={toAbs(car.images_urls[0])} alt={car.title} style={{ width: 72, height: 54, objectFit: 'cover', borderRadius: 6, display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />
                    : <div style={{ width: 72, height: 54, background: '#f0f0f0', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🚗</div>}
                </td>
                <td style={{ padding: '10px', minWidth: 140 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{car.title}</div>
                  <div style={{ color: '#888', fontSize: 12 }}>{car.brand} {car.model}</div>
                  {car.listing_status === 'live'  && <span style={{ fontSize: 11, background: '#e6f9f0', color: '#20c997', padding: '2px 6px', borderRadius: 4, marginTop: 3, display: 'inline-block' }}>🟢 Live</span>}
                  {car.listing_status === 'draft' && <span style={{ fontSize: 11, background: '#fff3cd', color: '#856404', padding: '2px 6px', borderRadius: 4, marginTop: 3, display: 'inline-block' }}>📝 Draft</span>}
                </td>
                <td style={{ padding: '10px', whiteSpace: 'nowrap' }}><strong>₹{car.price?.toLocaleString()}</strong></td>
                <td style={{ padding: '10px' }}>{car.year}</td>
                <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>{car.km_driven?.toLocaleString()} km</td>
                <td style={{ padding: '10px' }}>{car.location}</td>
                <td style={{ padding: '10px' }}>{getStatusBadge(car.status)}</td>
                <td style={{ padding: '10px' }}>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <ActionBtn color="#17a2b8" title="View"              onClick={() => handleView(car)}>👁️</ActionBtn>
                    <ActionBtn color="#003566" title="Edit"              onClick={() => handleEdit(car)}>✏️</ActionBtn>
                    <ActionBtn color="#6f42c1" title="Content & Publish" onClick={() => handleContent(car)}>📝</ActionBtn>
                    {car.inspection_status === 'pending' && <>
                      <ActionBtn color="#28a745" title="Approve" onClick={() => handleApprove(car.id)}>✓</ActionBtn>
                      <ActionBtn color="#dc3545" title="Reject"  onClick={() => handleReject(car.id)}>✕</ActionBtn>
                    </>}
                    {car.status === 'available' && <>
                      <ActionBtn color="#6c757d" title="Mark Sold" onClick={() => handleMarkSold(car.id)}>Sold</ActionBtn>
                      <ActionBtn color="#ffc107" title="Reserve"   onClick={() => handleReserve(car.id)}>Rsv</ActionBtn>
                    </>}
                    <ActionBtn color="#dc3545" title="Delete" onClick={() => handleDelete(car.id)}>🗑</ActionBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {displayedCars.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>No cars found</div>}
      </div>

      {showModal && (
        <CarModal
          mode={modalMode}
          car={selectedCar}
          onClose={() => { setShowModal(false); setSelectedCar(null); }}
          onSuccess={() => { setShowModal(false); setSelectedCar(null); loadCars(page, filter); }}
        />
      )}

      {/* ── Delete Confirmation Modal ── */}
    {showDeleteConfirm && (
      <div className="modal-overlay" onClick={cancelDelete}>
        <div
          onClick={e => e.stopPropagation()}
          style={{
            background: '#fff',
            borderRadius: 12,
            padding: '32px 28px',
            maxWidth: 420,
            width: '90vw',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 12 }}>🗑️</div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111', marginBottom: 8 }}>
            Delete Car?
          </h2>
          <p style={{ fontSize: 14, color: '#666', marginBottom: 24, lineHeight: 1.5 }}>
            This will permanently delete the car and remove it from the website.
            This action cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={cancelDelete}
              disabled={deleteLoading}
              style={{
                padding: '10px 24px',
                background: '#f0f0f0',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
                minWidth: 100,
              }}
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              disabled={deleteLoading}
              style={{
                padding: '10px 24px',
                background: deleteLoading ? '#ccc' : '#dc3545',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 14,
                cursor: deleteLoading ? 'not-allowed' : 'pointer',
                minWidth: 100,
              }}
            >
              {deleteLoading ? 'Deleting...' : 'Yes, Delete'}
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

// ── ActionBtn ─────────────────────────────────────────────────────────────────
const ActionBtn = ({ color, title, onClick, children }) => (
  <button onClick={onClick} title={title} style={{ padding: '5px 8px', border: 'none', borderRadius: 5, cursor: 'pointer', background: `${color}18`, color, fontSize: 13, fontWeight: 600, transition: 'all 0.15s', whiteSpace: 'nowrap' }}
    onMouseEnter={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = '#fff'; }}
    onMouseLeave={e => { e.currentTarget.style.background = `${color}18`; e.currentTarget.style.color = color; }}
  >{children}</button>
);

// ── CarModal ──────────────────────────────────────────────────────────────────
const CarModal = ({ mode, car, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '', brand: '', model: '', year: new Date().getFullYear(),
    price: '', location: '', mileage: '', km_driven: '',
    fuel_type: 'Petrol', variant: '', transmission: 'Manual', owner_count: 1,
    registration_number: '', insurance_validity: '', insurance_type: 'Comprehensive',
    rto: '', make_year: new Date().getFullYear(), engine_capacity: '',
    body_type: 'Sedan', description: '', status: 'available', inspection_status: 'approved',
  });

  const [images,         setImages]         = useState([]);
  const [exteriorImages, setExteriorImages] = useState([]);
  const [interiorImages, setInteriorImages] = useState([]);
  const [interiorVideo,  setInteriorVideo]  = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [activeTab,      setActiveTab]      = useState(mode === 'content' ? 'content' : 'details');
  const [lightbox,       setLightbox]       = useState(null);

  const [existingImages,         setExistingImages]         = useState([]);
  const [existingExteriorImages, setExistingExteriorImages] = useState([]);
  const [existingInteriorImages, setExistingInteriorImages] = useState([]);
  const [hasExistingVideo,       setHasExistingVideo]       = useState(false);
  const [existingVideoUrl,       setExistingVideoUrl]       = useState(null);
  const [deletingId,             setDeletingId]             = useState(null);

  useEffect(() => {
    if (car && mode !== 'create') {
      setFormData({
        title:               car.title               || '',
        brand:               car.brand               || '',
        model:               car.model               || '',
        year:                car.year                || new Date().getFullYear(),
        price:               car.price               || '',
        location:            car.location            || '',
        mileage:             car.mileage             || '',
        km_driven:           car.km_driven           || '',
        fuel_type:           car.fuel_type           || 'Petrol',
        variant:             car.variant             || '',
        transmission:        car.transmission        || 'Manual',
        owner_count:         car.owner_count         || 1,
        registration_number: car.registration_number || '',
        insurance_validity:  car.insurance_validity  || '',
        insurance_type:      car.insurance_type      || 'Comprehensive',
        rto:                 car.rto                 || '',
        make_year:           car.make_year           || new Date().getFullYear(),
        engine_capacity:     car.engine_capacity     || '',
        body_type:           car.body_type           || 'Sedan',
        description:         car.description         || '',
        status:              car.status              || 'available',
        inspection_status:   car.inspection_status   || 'approved',
      });

      adminCarService.getCarDetails(car.id).then(res => {
        const c = res.data;
        setExistingImages(c.images_attachments         || buildFallback(c.images_urls));
        setExistingExteriorImages(c.exterior_attachments || buildFallback(c.exterior_images_urls));
        setExistingInteriorImages(c.interior_attachments || buildFallback(c.interior_images_urls));
        setHasExistingVideo(!!c.interior_video_url);
        setExistingVideoUrl(c.interior_video_url || null);
      }).catch(() => {
        setExistingImages(buildFallback(car.images_urls));
        setExistingExteriorImages(buildFallback(car.exterior_images_urls));
        setExistingInteriorImages(buildFallback(car.interior_images_urls));
        setHasExistingVideo(!!car.interior_video_url);
        setExistingVideoUrl(car.interior_video_url || null);
      });
    }
  }, [car, mode]);

  const buildFallback = (urls = []) => (urls || []).map(url => ({ id: null, url }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDeleteExistingImage = async (attachmentType, item, setStateFn) => {
    if (!window.confirm('Remove this image permanently?')) return;
    if (!item.id) { alert('Cannot delete: attachment ID missing.'); return; }
    setDeletingId(item.id);
    try {
      await adminCarService.deleteAttachment(car.id, attachmentType, item.id);
      setStateFn(prev => prev.filter(a => a !== item));
    } catch (e) {
      alert('Failed to remove image: ' + (e.response?.data?.error || e.message));
    } finally { setDeletingId(null); }
  };

  const handleDeleteExistingVideo = async () => {
    if (!window.confirm('Remove the interior video permanently?')) return;
    setDeletingId('video');
    try {
      await adminCarService.deleteAttachment(car.id, 'interior_video');
      setHasExistingVideo(false);
      setExistingVideoUrl(null);
    } catch (e) {
      alert('Failed to remove video: ' + e.message);
    } finally { setDeletingId(null); }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);
    try {
      const carData = { ...formData, images, exterior_images: exteriorImages, interior_images: interiorImages };
      if (interiorVideo) carData.interior_video = interiorVideo;
      if (mode === 'create') {
        await adminCarService.createCar(carData);
        alert('Car created successfully');
      } else {
        await adminCarService.updateCar(car.id, carData);
        alert('Car updated successfully');
      }
      onSuccess();
    } catch (error) {
      alert(`Failed to ${mode} car: ` + (error.response?.data?.errors || error.message));
    } finally { setSaving(false); }
  };

  const tabs = mode === 'view'
    ? [{ id: 'details', label: '📋 Details' }, { id: 'media', label: '🖼️ Media' }]
    : mode === 'content'
    ? [{ id: 'content', label: '📝 Content & Publish' }]
    : [{ id: 'details', label: '📋 Details' }, { id: 'media', label: '🖼️ Media & Video' }];

  const tStyle = (t) => ({
    padding: '11px 20px', background: 'transparent', border: 'none',
    borderBottom: `3px solid ${activeTab === t ? '#003566' : 'transparent'}`,
    cursor: 'pointer', fontWeight: activeTab === t ? 700 : 500,
    color: activeTab === t ? '#003566' : '#666', fontSize: 14,
  });

  const modalTitle = { view: 'View Car Details', edit: 'Edit Car', create: 'Add New Car', content: 'Content & Publish' }[mode];

  // Grid layout for the form — 2 columns, fully inline
  const GRID = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px 20px',
    padding: '4px 2px',
  };

  return (
    <>
      {lightbox && <Lightbox src={lightbox.src} type={lightbox.type} onClose={() => setLightbox(null)} />}

      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content car-modal-large" onClick={e => e.stopPropagation()}
          style={{ maxWidth: 860, width: '95vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>

          <div className="modal-header">
            <h2>{modalTitle}</h2>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>

          <div style={{ display: 'flex', borderBottom: '2px solid #e8e8e8', background: '#f8f8f8' }}>
            {tabs.map(t => <button key={t.id} style={tStyle(t.id)} onClick={() => setActiveTab(t.id)}>{t.label}</button>)}
          </div>

          <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>

            {/* ── VIEW: Details ── */}
            {mode === 'view' && activeTab === 'details' && (
              <div className="details-grid">
                {[
                  ['Title', car?.title], ['Brand', car?.brand], ['Model', car?.model],
                  ['Year', car?.year], ['Price', `₹${car?.price?.toLocaleString()}`],
                  ['Location', car?.location], ['KM Driven', `${car?.km_driven?.toLocaleString()} km`],
                  ['Fuel Type', car?.fuel_type], ['Transmission', car?.transmission],
                  ['Owner Count', car?.owner_count], ['Body Type', car?.body_type],
                  ['Status', car?.status?.replace(/_/g, ' ')],
                  ['Listing Status', car?.listing_status],
                  ['Content Verified', car?.content_verified ? 'Yes ✅' : 'No'],
                  ['Description', car?.description],
                ].map(([label, value]) => (
                  <div className="detail-item" key={label}>
                    <label>{label}</label>
                    <p style={{ textTransform: label === 'Description' ? 'none' : 'capitalize' }}>{value || '—'}</p>
                  </div>
                ))}
              </div>
            )}

            {/* ── VIEW: Media ── */}
            {mode === 'view' && activeTab === 'media' && (
              <div>
                <ClickableMediaSection title="📸 Main Images"     urls={car?.images_urls}         onOpen={url => setLightbox({ src: url, type: 'image' })} />
                <ClickableMediaSection title="🚗 Exterior Images" urls={car?.exterior_images_urls} onOpen={url => setLightbox({ src: url, type: 'image' })} />
                <ClickableMediaSection title="🪑 Interior Images" urls={car?.interior_images_urls} onOpen={url => setLightbox({ src: url, type: 'image' })} />
                <h3 style={{ marginBottom: 10, color: '#003566' }}>🎬 Interior Video</h3>
                {car?.interior_video_url ? (
                  <div onClick={() => setLightbox({ src: car.interior_video_url, type: 'video' })} style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}>
                    <video src={toAbs(car.interior_video_url)} style={{ width: 280, height: 170, borderRadius: 10, objectFit: 'cover', border: '2px solid #e8e8e8', display: 'block' }} />
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', borderRadius: 10 }}>
                      <span style={{ fontSize: 48, color: '#fff' }}>▶</span>
                    </div>
                    <p style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Click to play fullscreen</p>
                  </div>
                ) : <p style={{ color: '#999' }}>No interior video uploaded.</p>}
              </div>
            )}

            {/* ── CREATE / EDIT: Details ── */}
            {(mode === 'create' || mode === 'edit') && activeTab === 'details' && (
              <div style={GRID}>
                <FG label="Title *">        <input name="title"       value={formData.title}       onChange={handleChange} required placeholder="e.g., Maruti Swift VXI" /></FG>
                <FG label="Brand *">        <input name="brand"       value={formData.brand}       onChange={handleChange} required placeholder="e.g., Maruti Suzuki" /></FG>
                <FG label="Model *">        <input name="model"       value={formData.model}       onChange={handleChange} required placeholder="e.g., Swift" /></FG>
                <FG label="Year *">         <input name="year"        value={formData.year}        onChange={handleChange} required type="number" min="1900" max={new Date().getFullYear() + 1} /></FG>
                <FG label="Make Years">
                <input
                  name="make_year"
                  value={formData.make_year}
                  onChange={handleChange}
                  type="number"
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  placeholder="e.g., 2022"
                />
              </FG>
                <FG label="Price (₹) *">   <input name="price"       value={formData.price}       onChange={handleChange} required type="number" placeholder="e.g., 500000" /></FG>
                <FG label="Location *">    <input name="location"    value={formData.location}    onChange={handleChange} required placeholder="e.g., Delhi" /></FG>
                <FG label="KM Driven *">   <input name="km_driven"   value={formData.km_driven}   onChange={handleChange} required type="number" placeholder="e.g., 25000" /></FG>
                <FG label="Mileage (km/l)"><input name="mileage"     value={formData.mileage}     onChange={handleChange} type="number" placeholder="e.g., 18" /></FG>
                <FG label="Fuel Type *">
                  <select name="fuel_type" value={formData.fuel_type} onChange={handleChange}>
                    {['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid'].map(v => <option key={v}>{v}</option>)}
                  </select>
                </FG>
                <FG label="Transmission *">
                  <select name="transmission" value={formData.transmission} onChange={handleChange}>
                    <option>Manual</option><option>Automatic</option>
                  </select>
                </FG>
                <FG label="Owner Count"><input name="owner_count"    value={formData.owner_count}    onChange={handleChange} type="number" min="1" /></FG>
                <FG label="Body Type">
                  <select name="body_type" value={formData.body_type} onChange={handleChange}>
                    {['Sedan', 'SUV', 'Hatchback', 'Coupe', 'Convertible', 'Wagon', 'MUV'].map(v => <option key={v}>{v}</option>)}
                  </select>
                </FG>
                <FG label="Variant">            <input name="variant"             value={formData.variant}             onChange={handleChange} placeholder="e.g., VXI" /></FG>
                <FG label="Registration Number"><input name="registration_number" value={formData.registration_number} onChange={handleChange} placeholder="e.g., DL01AB1234" /></FG>
                <FG label="Insurance Validity"> <input name="insurance_validity"  value={formData.insurance_validity}  onChange={handleChange} type="date" /></FG>
                <FG label="Insurance Type">
                  <select name="insurance_type" value={formData.insurance_type} onChange={handleChange}>
                    <option>Comprehensive</option><option>Third Party</option><option>Expired</option>
                  </select>
                </FG>
                <FG label="RTO">             <input name="rto"             value={formData.rto}             onChange={handleChange} placeholder="e.g., DL01" /></FG>
                <FG label="Engine Cap (cc)"> <input name="engine_capacity" value={formData.engine_capacity} onChange={handleChange} type="number" placeholder="e.g., 1200" /></FG>
                <FG label="Status">
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="available">Available</option>
                    <option value="sold">Sold</option>
                    <option value="reserved">Reserved</option>
                    <option value="under_inspection">Under Inspection</option>
                  </select>
                </FG>
                <FG label="Description" full>
                  <textarea name="description" value={formData.description} onChange={handleChange} rows="3" placeholder="Add description..." />
                </FG>
              </div>
            )}

            {/* ── CREATE / EDIT: Media ── */}
            {(mode === 'create' || mode === 'edit') && activeTab === 'media' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <EditableMediaUpload
                  label="📸 Main Images"
                  newFiles={images}
                  onNewFilesChange={e => setImages(Array.from(e.target.files))}
                  existing={existingImages}
                  onDeleteExisting={(item) => handleDeleteExistingImage('images', item, setExistingImages)}
                  onOpen={url => setLightbox({ src: url, type: 'image' })}
                  deletingId={deletingId}
                />
                <EditableMediaUpload
                  label="🚗 Exterior Images"
                  newFiles={exteriorImages}
                  onNewFilesChange={e => setExteriorImages(Array.from(e.target.files))}
                  existing={existingExteriorImages}
                  onDeleteExisting={(item) => handleDeleteExistingImage('exterior_images', item, setExistingExteriorImages)}
                  onOpen={url => setLightbox({ src: url, type: 'image' })}
                  deletingId={deletingId}
                />
                <EditableMediaUpload
                  label="🪑 Interior Images"
                  newFiles={interiorImages}
                  onNewFilesChange={e => setInteriorImages(Array.from(e.target.files))}
                  existing={existingInteriorImages}
                  onDeleteExisting={(item) => handleDeleteExistingImage('interior_images', item, setExistingInteriorImages)}
                  onOpen={url => setLightbox({ src: url, type: 'image' })}
                  deletingId={deletingId}
                />

                {/* Interior Video */}
                <div>
                  <label style={LABEL_STYLE}>🎬 Interior Video</label>
                  <input type="file" accept="video/mp4,video/webm,video/ogg,video/quicktime"
                    onChange={e => setInteriorVideo(e.target.files[0] || null)}
                    style={{ ...FIELD_STYLE, borderStyle: 'dashed', background: '#fafafa', cursor: 'pointer' }}
                  />
                  {interiorVideo && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                      <small style={{ color: '#28a745' }}>✅ {interiorVideo.name} ({(interiorVideo.size / 1024 / 1024).toFixed(1)} MB)</small>
                      <button onClick={() => setInteriorVideo(null)} style={{ background: '#dc354520', color: '#dc3545', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>✕ Clear</button>
                    </div>
                  )}
                  {mode === 'edit' && hasExistingVideo && existingVideoUrl && !interiorVideo && (
                    <div style={{ marginTop: 10 }}>
                      <p style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>Current video:</p>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{ position: 'relative', cursor: 'pointer', display: 'inline-block' }}
                          onClick={() => setLightbox({ src: existingVideoUrl, type: 'video' })} title="Click to play fullscreen">
                          <video src={toAbs(existingVideoUrl)} style={{ width: 200, height: 120, borderRadius: 8, objectFit: 'cover', display: 'block' }} />
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)', borderRadius: 8 }}>
                            <span style={{ fontSize: 36, color: '#fff' }}>▶</span>
                          </div>
                        </div>
                        <button
                          onClick={handleDeleteExistingVideo}
                          disabled={deletingId === 'video'}
                          style={{ padding: '6px 12px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12, marginTop: 4, opacity: deletingId === 'video' ? 0.6 : 1 }}
                        >
                          {deletingId === 'video' ? '⏳ Removing...' : '🗑 Remove Video'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── CONTENT & PUBLISH ── */}
            {mode === 'content' && activeTab === 'content' && (
              <ContentPublishPanel car={car} onSuccess={onSuccess} />
            )}
          </div>

          {(mode === 'create' || mode === 'edit') && activeTab === 'details' && (
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e8e8e8', display: 'flex', gap: 10, justifyContent: 'flex-end', background: '#fafafa' }}>
              <button
                disabled={saving}
                onClick={handleSubmit}
                style={{ padding: '8px 20px', background: '#003566', color: '#FFD60A', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14, minWidth: 120, opacity: saving ? 0.7 : 1 }}
              >
                {saving ? 'Saving...' : mode === 'create' ? 'Create Car' : 'Update Car'}
              </button>
              <button
                onClick={onClose}
                style={{ padding: '8px 20px', background: '#003566', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14, minWidth: 120 }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ── EditableMediaUpload ───────────────────────────────────────────────────────
const EditableMediaUpload = ({ label, newFiles, onNewFilesChange, existing = [], onDeleteExisting, onOpen, deletingId }) => (
  <div>
    <label style={LABEL_STYLE}>{label}</label>
    {existing.length > 0 && (
      <div style={{ marginBottom: 10 }}>
        <p style={{ fontSize: 12, color: '#555', marginBottom: 6, fontWeight: 600 }}>
          Current images ({existing.length}) — click 🔍 to view, ✕ to delete:
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {existing.map((item, i) => {
            const url = typeof item === 'string' ? item : item.url;
            const key = item.id || url || i;
            const isDeleting = deletingId === (item.id || item.url);
            return (
              <div key={key} style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={toAbs(url)} alt={`img-${i}`}
                  onClick={() => onOpen(url)}
                  style={{ width: 90, height: 68, objectFit: 'cover', borderRadius: 7, border: '2px solid #ddd', display: 'block', cursor: 'pointer', opacity: isDeleting ? 0.4 : 1 }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#003566'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#ddd'}
                  onError={e => { e.target.style.opacity = 0.2; }}
                />
                <div style={{ position: 'absolute', bottom: 20, right: 2, background: 'rgba(0,0,0,0.5)', color: '#fff', borderRadius: 3, fontSize: 10, padding: '1px 4px', pointerEvents: 'none' }}>🔍</div>
                <button
                  onClick={() => onDeleteExisting(item)}
                  disabled={!!deletingId}
                  title="Delete this image"
                  style={{ position: 'absolute', top: 2, right: 2, background: isDeleting ? 'rgba(150,150,150,0.85)' : 'rgba(220,53,69,0.9)', border: 'none', color: '#fff', borderRadius: '50%', width: 20, height: 20, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, lineHeight: 1, padding: 0 }}
                >
                  {isDeleting ? '…' : '✕'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    )}
    <input type="file" accept="image/*" multiple onChange={onNewFilesChange}
      style={{ ...FIELD_STYLE, borderStyle: 'dashed', background: '#fafafa', cursor: 'pointer' }}
    />
    <small style={{ color: '#888', fontSize: 11, marginTop: 4, display: 'block' }}>{newFiles.length} new file(s) selected</small>
  </div>
);

// ── ClickableMediaSection (view mode) ────────────────────────────────────────
const ClickableMediaSection = ({ title, urls, onOpen }) => (
  <div style={{ marginBottom: 20 }}>
    <h3 style={{ marginBottom: 10, color: '#003566' }}>{title}</h3>
    {urls?.length > 0 ? (
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {urls.map((url, i) => (
          <div key={i} style={{ position: 'relative', cursor: 'pointer' }} onClick={() => onOpen(url)} title="Click to view fullscreen">
            <img src={toAbs(url)} alt={`img-${i}`}
              style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 8, border: '2px solid #e8e8e8', display: 'block' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#003566'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#e8e8e8'}
              onError={e => { e.target.style.display = 'none'; }}
            />
            <div style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.5)', color: '#fff', borderRadius: 4, fontSize: 11, padding: '1px 5px' }}>🔍</div>
          </div>
        ))}
      </div>
    ) : <p style={{ color: '#999', fontSize: 13 }}>No images uploaded.</p>}
  </div>
);

// ── Content & Publish Panel ───────────────────────────────────────────────────
const ContentPublishPanel = ({ car, onSuccess }) => {
  const [section,     setSection]     = useState('features');
  const [verifying,   setVerifying]   = useState(false);
  const [publishing,  setPublishing]  = useState(false);
  const [statusMsg,   setStatusMsg]   = useState('');

  const [features,    setFeatures]    = useState([]);
  const [featureForm, setFeatureForm] = useState({ category: 'Safety', name: '', available: true });
  const [editFeatId,  setEditFeatId]  = useState(null);

  const [benefits,    setBenefits]    = useState([]);
  const [benefitForm, setBenefitForm] = useState({ title: '', description: '', included: true });
  const [editBenId,   setEditBenId]   = useState(null);

  const [specs, setSpecs] = useState({
    mileage: '', engine_displacement: '', power: '', torque: '',
    ground_clearance: '', boot_space: '', fuel_tank_capacity: '',
    wheelbase: '', length: '', width: '', height: '',
    front_tyre_size: '', rear_tyre_size: '', number_of_doors: '',
    gear_box: '', drive_train: '', number_of_gears: '',
    number_of_cylinders: '', turbocharger: '',
    steering_type: '', front_brake_type: '', rear_brake_type: '',
    emission_class: '',
  });
  const [savingSpecs, setSavingSpecs] = useState(false);
  const [carData,     setCarData]     = useState(car);
  const carId = carData?.id;

  useEffect(() => {
    if (!carId) return;
    adminCarFeaturesService.getFeatures(carId).then(r => setFeatures(r.features || [])).catch(() => {});
    adminCarBenefitsService.getBenefits(carId).then(r => setBenefits(r.benefits || [])).catch(() => {});
    adminCarSpecificationsService.getSpecification(carId)
      .then(r => { if (r.specification) setSpecs(s => ({ ...s, ...r.specification })); })
      .catch(() => {});
  }, [carId]);

  const saveFeature = async () => {
    if (!featureForm.name) return alert('Feature name required');
    try {
      if (editFeatId) await adminCarFeaturesService.updateFeature(carId, editFeatId, featureForm);
      else             await adminCarFeaturesService.createFeature(carId, featureForm);
      setFeatureForm({ category: 'Safety', name: '', available: true }); setEditFeatId(null);
      adminCarFeaturesService.getFeatures(carId).then(r => setFeatures(r.features || []));
    } catch (e) { alert('Failed: ' + e.message); }
  };
  const delFeature = async (id) => {
    if (!window.confirm('Delete?')) return;
    await adminCarFeaturesService.deleteFeature(carId, id);
    adminCarFeaturesService.getFeatures(carId).then(r => setFeatures(r.features || []));
  };

  const saveBenefit = async () => {
    if (!benefitForm.title) return alert('Benefit title required');
    try {
      if (editBenId) await adminCarBenefitsService.updateBenefit(carId, editBenId, benefitForm);
      else            await adminCarBenefitsService.createBenefit(carId, benefitForm);
      setBenefitForm({ title: '', description: '', included: true }); setEditBenId(null);
      adminCarBenefitsService.getBenefits(carId).then(r => setBenefits(r.benefits || []));
    } catch (e) { alert('Failed: ' + e.message); }
  };
  const delBenefit = async (id) => {
    if (!window.confirm('Delete?')) return;
    await adminCarBenefitsService.deleteBenefit(carId, id);
    adminCarBenefitsService.getBenefits(carId).then(r => setBenefits(r.benefits || []));
  };

  const saveSpecs = async () => {
    setSavingSpecs(true);
    try { await adminCarSpecificationsService.upsertSpecification(carId, specs); alert('Specifications saved!'); }
    catch { alert('Failed to save specs'); }
    finally { setSavingSpecs(false); }
  };

  const handleVerify = async () => {
    setVerifying(true); setStatusMsg('');
    try {
      await adminCarContentService.verifyContent(carId);
      setCarData(prev => ({ ...prev, content_verified: true }));
      setStatusMsg('✅ Content verified! You can now publish the car.');
    } catch (e) { setStatusMsg('❌ ' + (e.response?.data?.message || e.message)); }
    finally { setVerifying(false); }
  };

  const handlePublish = async () => {
    if (!window.confirm('Publish this car? It will be visible to all buyers.')) return;
    setPublishing(true); setStatusMsg('');
    try {
      await adminCarPublishService.publishCar(carId);
      setCarData(prev => ({ ...prev, listing_status: 'live' }));
      setStatusMsg('🚀 Car is now LIVE on the platform!');
      onSuccess();
    } catch (e) { setStatusMsg('❌ ' + (e.response?.data?.message || e.message)); }
    finally { setPublishing(false); }
  };

  const cats = ['Safety', 'Comfort', 'Entertainment', 'Technology', 'Exterior', 'Interior'];
  const secBtn = (id, label) => (
    <button onClick={() => setSection(id)} style={{ padding: '8px 14px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, background: section === id ? '#003566' : '#f0f0f0', color: section === id ? '#FFD60A' : '#333' }}>{label}</button>
  );

  return (
    <div>
      <div style={{ background: '#f0f4ff', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <div style={{ flex: 1, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <SPill label="Content"  ok={carData?.content_verified} />
          <SPill label="Platform" ok={carData?.owned_by_platform} />
          <SPill label="Listing"  ok={carData?.listing_status === 'live'} text={carData?.listing_status} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!carData?.content_verified && (
            <button disabled={verifying} onClick={handleVerify} style={{ padding: '8px 16px', background: '#6f42c1', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              {verifying ? '⏳ Verifying...' : '✅ Verify Content'}
            </button>
          )}
          {carData?.content_verified && carData?.listing_status !== 'live' && (
            <button disabled={publishing} onClick={handlePublish} style={{ padding: '8px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              {publishing ? '⏳ Publishing...' : '🚀 Publish Car'}
            </button>
          )}
          {carData?.listing_status === 'live' && (
            <span style={{ padding: '8px 16px', background: '#e6f9f0', color: '#20c997', borderRadius: 6, fontWeight: 700, fontSize: 13 }}>🟢 Live on Platform</span>
          )}
        </div>
      </div>

      {statusMsg && (
        <div style={{ marginBottom: 12, padding: '10px 14px', background: statusMsg.startsWith('❌') ? '#fff5f5' : '#f0fff4', border: `1px solid ${statusMsg.startsWith('❌') ? '#ffc0c0' : '#c6f6d5'}`, borderRadius: 6, fontSize: 13 }}>
          {statusMsg}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {secBtn('features', '🚗 Features')}
        {secBtn('benefits', '💎 Benefits')}
        {secBtn('specs',    '⚙️ Specifications')}
      </div>

      {section === 'features' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, color: '#666' }}>Category</label>
              <select value={featureForm.category} onChange={e => setFeatureForm({ ...featureForm, category: e.target.value })} style={{ padding: '6px 8px', borderRadius: 5, border: '1px solid #ccc', fontSize: 13 }}>
                {cats.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 140 }}>
              <label style={{ fontSize: 11, color: '#666' }}>Name *</label>
              <input value={featureForm.name} onChange={e => setFeatureForm({ ...featureForm, name: e.target.value })} placeholder="e.g., Airbags" style={{ padding: '6px 8px', borderRadius: 5, border: '1px solid #ccc', fontSize: 13 }} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={featureForm.available} onChange={e => setFeatureForm({ ...featureForm, available: e.target.checked })} /> Available
            </label>
            <button onClick={saveFeature} style={{ padding: '6px 14px', background: '#003566', color: '#FFD60A', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
              {editFeatId ? '✏️ Update' : '➕ Add'}
            </button>
            {editFeatId && <button onClick={() => { setFeatureForm({ category: 'Safety', name: '', available: true }); setEditFeatId(null); }} style={{ padding: '6px 10px', background: '#eee', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13 }}>Cancel</button>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(165px,1fr))', gap: 8 }}>
            {features.map(f => (
              <div key={f.id} style={{ padding: '8px 10px', background: '#f8f8f8', borderRadius: 6, border: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                <span>{f.available ? '✅' : '❌'} <strong>{f.name}</strong> <span style={{ color: '#999', fontSize: 11 }}>{f.category}</span></span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => { setFeatureForm({ category: f.category, name: f.name, available: f.available }); setEditFeatId(f.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>✏️</button>
                  <button onClick={() => delFeature(f.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>🗑</button>
                </div>
              </div>
            ))}
          </div>
          {features.length === 0 && <p style={{ color: '#999', fontSize: 13, marginTop: 8 }}>No features yet.</p>}
        </div>
      )}

      {section === 'benefits' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 140 }}>
              <label style={{ fontSize: 11, color: '#666' }}>Title *</label>
              <input value={benefitForm.title} onChange={e => setBenefitForm({ ...benefitForm, title: e.target.value })} placeholder="e.g., Extended Warranty" style={{ padding: '6px 8px', borderRadius: 5, border: '1px solid #ccc', fontSize: 13 }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 2, minWidth: 180 }}>
              <label style={{ fontSize: 11, color: '#666' }}>Description</label>
              <input value={benefitForm.description} onChange={e => setBenefitForm({ ...benefitForm, description: e.target.value })} placeholder="Brief description..." style={{ padding: '6px 8px', borderRadius: 5, border: '1px solid #ccc', fontSize: 13 }} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={benefitForm.included} onChange={e => setBenefitForm({ ...benefitForm, included: e.target.checked })} /> Included
            </label>
            <button onClick={saveBenefit} style={{ padding: '6px 14px', background: '#003566', color: '#FFD60A', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
              {editBenId ? '✏️ Update' : '➕ Add'}
            </button>
            {editBenId && <button onClick={() => { setBenefitForm({ title: '', description: '', included: true }); setEditBenId(null); }} style={{ padding: '6px 10px', background: '#eee', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13 }}>Cancel</button>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {benefits.map(b => (
              <div key={b.id} style={{ padding: '10px 12px', background: '#f8f8f8', borderRadius: 6, border: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{b.included ? '✅' : '❌'} {b.title}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>{b.description}</div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => { setBenefitForm({ title: b.title, description: b.description, included: b.included }); setEditBenId(b.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>✏️</button>
                  <button onClick={() => delBenefit(b.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>🗑</button>
                </div>
              </div>
            ))}
          </div>
          {benefits.length === 0 && <p style={{ color: '#999', fontSize: 13, marginTop: 8 }}>No benefits yet.</p>}
        </div>
      )}

      {section === 'specs' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(190px,1fr))', gap: 10, marginBottom: 14 }}>
            {[
              ['mileage', 'Mileage (km/l)'], ['engine_displacement', 'Engine (cc)'],
              ['power', 'Power (bhp)'], ['torque', 'Torque (Nm)'],
              ['ground_clearance', 'Ground Clearance (mm)'], ['boot_space', 'Boot Space (L)'],
              ['fuel_tank_capacity', 'Fuel Tank (L)'], ['wheelbase', 'Wheelbase (mm)'],
              ['length', 'Length (mm)'], ['width', 'Width (mm)'], ['height', 'Height (mm)'],
              ['front_tyre_size', 'Front Tyre'], ['rear_tyre_size', 'Rear Tyre'],
              ['number_of_doors', 'Doors'], ['number_of_gears', 'Gears'],
              ['number_of_cylinders', 'Cylinders'],
            ].map(([key, label]) => (
              <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <label style={{ fontSize: 11, color: '#666' }}>{label}</label>
                <input value={specs[key] || ''} onChange={e => setSpecs({ ...specs, [key]: e.target.value })} placeholder="—" style={{ padding: '6px 8px', borderRadius: 5, border: '1px solid #ccc', fontSize: 13 }} />
              </div>
            ))}
            {[
              ['gear_box', 'Gearbox', ['', 'Manual', 'Automatic', 'AMT', 'CVT', 'DCT']],
              ['drive_train', 'Drive Train', ['', 'FWD', 'RWD', 'AWD', '4WD']],
              ['turbocharger', 'Turbocharger', ['', 'Yes', 'No']],
              ['steering_type', 'Steering', ['', 'Power', 'Electric', 'Manual']],
              ['front_brake_type', 'Front Brake', ['', 'Disc', 'Drum', 'Ventilated Disc']],
              ['rear_brake_type', 'Rear Brake', ['', 'Disc', 'Drum']],
              ['emission_class', 'Emission', ['', 'BS6', 'BS4', 'Euro 6']],
            ].map(([key, label, opts]) => (
              <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <label style={{ fontSize: 11, color: '#666' }}>{label}</label>
                <select value={specs[key] || ''} onChange={e => setSpecs({ ...specs, [key]: e.target.value })} style={{ padding: '6px 8px', borderRadius: 5, border: '1px solid #ccc', fontSize: 13 }}>
                  {opts.map(o => <option key={o} value={o}>{o || 'Select'}</option>)}
                </select>
              </div>
            ))}
          </div>
          <button onClick={saveSpecs} disabled={savingSpecs} style={{ padding: '8px 20px', background: '#003566', color: '#FFD60A', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
            {savingSpecs ? '⏳ Saving...' : '💾 Save Specifications'}
          </button>
        </div>
      )}

    </div>
  );
};

const SPill = ({ label, ok, text }) => (
  <span style={{ fontSize: 12, padding: '3px 8px', borderRadius: 10, background: ok ? '#e6f9f0' : '#fff3cd', color: ok ? '#20c997' : '#856404', fontWeight: 600 }}>
    {ok ? '✅' : '⏳'} {label}{text ? `: ${text}` : ''}
  </span>
);

export default CarsManagement;